<template>
  <OrderTestDialog
    class="build-bundle-dialog"
    :title="title"
    :is-show-dialog="isShowDialog"
    :width="1100"
    :show-footer="false"
    @close="$emit('close')"
  >
    <div class="build-bundle-dialog-content">
      <BuildBundleBanner 
        :selected-zoomer-count="selectedZoomerIds.length"
        :selected-panel-count="selectedPanelIds.length"
        :selected-zoomer-names="selectedZoomerNames"
        :selected-panel-names="selectedPanelNames"
        :zoomer-quantity="zoomerQuantity"
        :panel-quantity="panelQuantity"
        :total-price="totalPrice"
        :saved-amount="savedAmount"
        :promo-price="promoPrice"
      />

      <div v-if="showDbsSwitch" class="home-collection-toggle-wrapper mt-3">
        <div class="home-collection-label-wrapper">
          <span class="home-collection-toggle-text">Show Only Tests Available for Home Collection</span>
        </div>
        <div class="status-toggle">
          <span class="status-label" :class="{ active: !dbsToggle }">OFF</span>
          <div class="toggle-switch" :class="{ active: dbsToggle, disabled: isDbsToggleDisabled }" @click="toggleHomeCollection">
            <div class="toggle-track">
              <div class="toggle-thumb"></div>
            </div>
          </div>
          <span class="status-label" :class="{ active: dbsToggle }">ON</span>
        </div>
      </div>

      <div class="bundle-selection-scroll" :class="{ 'has-panels': panelQuantity > 0 }">
        <div class="selection-block mb-1.5 mt-4">
          <p class="section-title mb-3 h-4 w-full">
            Select zoomers ({{ selectedZoomerIds.length }}/{{ zoomerQuantity }}):
          </p>
          <div class="grid grid-cols-3 gap-3">
            <BuildBundleTestCard
              v-for="test in computedZoomerList"
              :key="test.id"
              :test="test"
              :is-selected="selectedZoomerIds.includes(test.id)"
              :is-disabled="!isAllowedForPatientAge(test) || isItemDisabledByOtherItems(test) || isZoomerInXChooseY(test) || (!selectedZoomerIds.includes(test.id) && remainingZoomerQuantity === 0)"
              @click="handleSelectItem(test, 'zoomer')"
            />
          </div>
          <p v-if="computedZoomerList.length === 0" class="empty-state-text">
            No zoomers available in this bundle.
          </p>
        </div>

        <div v-if="panelQuantity > 0" class="selection-block selection-section mt-4">
          <p class="section-title mb-3 h-4 w-full">
            Select panels ({{ selectedPanelIds.length }}/{{ panelQuantity }}):
          </p>

          <div v-for="category in computedPanelCategories" :key="category.id" class="mb-1.5">
            <div class="px-3 category-title">
              <p>{{ category.name }}</p>
            </div>
            <div class="grid grid-cols-3 gap-4 mt-1 p-2">
              <BuildBundleTestCard
                v-for="panel in category.panels"
                :key="panel.id || panel.bundleId"
                :test="panel"
                :is-selected="selectedPanelIds.includes(panel.id || panel.bundleId)"
                :is-disabled="getPanelCardDisabled(panel, category)"
                @click="handleSelectItem(panel, 'panel')"
              />
            </div>
          </div>

          <p v-if="computedPanelCategories.length === 0" class="empty-state-text">
            No panels available in this bundle.
          </p>
        </div>
      </div>

      <div class="modal-footer-actions">
        <LoginButton
          type="primary"
          plain
          class="build-bundle-back-btn"
          @click="handleCancel"
        >
          Back
        </LoginButton>
        <LoginButton
          type="primary"
          class="build-bundle-add-btn"
          :disabled="!canAddToCart"
          @click="handleAddToCart"
        >
          Add to Cart
        </LoginButton>
      </div>
    </div>
  </OrderTestDialog>
</template>

<script>
import OrderTestDialog from "../../../components/OrderTestDialog.vue";
import BuildBundleTestCard from "./BuildBundleTestCard.vue";
import BuildBundleBanner from "./BuildBundleBanner.vue";
import LoginButton from "../../../../login/components/LoginButton.vue";
import { isItemAllowedForPatientAge } from "../../custom-panel/customPanelCard";
import { mapState, mapGetters } from "vuex";
import { getAllDescendants } from "@/utils/va-order-test/testAndPanelSelection/testSelection";
import { applyConditionalDiscounts } from "@/utils/va-order-test/index.js";
import { cloneOrderTestTreeAndBundles } from "@/store/modules/va-order-test/helpers/deepCopyHelpers";
import { getTestIdsForBestDealFunc } from "@/utils/va-order-test/bestDealSuggestion";
import { getSelectedTestIdsInSuggestion } from "@/views/va-orderTest/order-page/order-summary/selected-tests-tab-pane/index.js";

export default {
  name: "BuildBundleDialog",
  components: {
    OrderTestDialog,
    BuildBundleTestCard,
    BuildBundleBanner,
    LoginButton
  },
  props: {
    title: {
      type: String,
      default: "Build Bundle"
    },
    isShowDialog: {
      type: Boolean,
      default: false
    },
    bundleId: {
      type: Number,
      default: null
    }
  },
  emits: ["close", "cancel", "add-to-cart"],
  data() {
    const deepCopies = cloneOrderTestTreeAndBundles();
    
    return {
      dbsToggle: this.$store.state.orderTest.isDbsVersion,
      selectedZoomerIds: [],
      selectedPanelIds: [],
      currentBundleId: null,
      /** Suggestion result from initializeSelection (selectedMainIds, selectedSupplementIds) for xChooseY filtering */
      suggestionPrefillResult: null,
      ...deepCopies,
    };
  },
  computed: {
    ...mapState("orderTest", ["dbsMap", "isAddOnOrder"]),
    ...mapState("orderTest/exceptions", ["exceptionMap"]),
    ...mapState("orderTest/user", ["userInfo"]),
    ...mapGetters("orderTest", ["orderTestSupportObj"]),
    ...mapGetters("orderTest/favorites", ["itemsInSelectedShortcuts"]),
    ...mapGetters("orderTest/panels", ["selectedXChooseYPanelList"]),
    ...mapGetters("orderTest/order", ["patientAge"]),
    
    // Get the current bundle based on dbsToggle and bundleId
    currentBundle() {
      if (!this.currentBundleId) return null;
      
      // Find the bundle by currentBundleId
      let bundle = this.bundleList.find(b => b.bundleId === this.currentBundleId);
      if (!bundle) return null;
      
      // If bundle section matches current toggle, use it
      const bundleSection = bundle.section;
      if (bundleSection === 'both' || 
          (this.dbsToggle && bundleSection === 'at_home') ||
          (!this.dbsToggle && bundleSection === 'not_at_home')) {
        return bundle;
      }
      
      // Otherwise, find the corresponding bundle version using dbsMap
      if (this.dbsToggle) {
        // Switching to DBS: find DBS version
        const dbsMapping = this.dbsMap.find(dbs => dbs.originalItemId === this.currentBundleId && dbs.type === 'bundle');
        if (dbsMapping) {
          const dbsBundle = this.bundleList.find(b => b.bundleId === dbsMapping.dbsItemId);
          return dbsBundle || bundle;
        }
      } else {
        // Switching to serum: find serum version
        const dbsMapping = this.dbsMap.find(dbs => dbs.dbsItemId === this.currentBundleId && dbs.type === 'bundle');
        if (dbsMapping) {
          const serumBundle = this.bundleList.find(b => b.bundleId === dbsMapping.originalItemId);
          return serumBundle || bundle;
        }
      }
      
      return bundle;
    },
    
    zoomerQuantity() {
      return this.currentBundle?.quantity || 0;
    },
    panelQuantity() {
      return this.currentBundle?.supplementQuantity || 0;
    },
    
    remainingZoomerQuantity() {
      return Math.max(0, this.zoomerQuantity - this.selectedZoomerIds.length);
    },
    remainingPanelQuantity() {
      return Math.max(0, this.panelQuantity - this.selectedPanelIds.length);
    },

    supportObj() {
      return {
        testNodeList: this.bfsTestList,
        vaRootNodeList: this.vaRootNodeList,
        vwRootNodeList: this.vwRootNodeList,
        bundleList: this.bundleList,
        exceptionMap: this.exceptionMap,
        dbsMap: this.dbsMap,
      };
    },

    canAddToCart() {
      return this.selectedZoomerIds.length >= this.zoomerQuantity && 
             this.selectedPanelIds.length >= this.panelQuantity;
    },
    
    totalPrice() {
      // Calculate price for all selected items (heavy metal price is already updated directly)
      return this.selectedItemsList.reduce((sum, item) => sum + item.price, 0);
    },
    
    savedAmount() {
      if (this.selectedZoomerIds.length >= this.zoomerQuantity && 
          this.selectedPanelIds.length >= this.panelQuantity) {
        return Math.max(0, this.totalPrice - this.promoPrice);
      }
      return 0;
    },

    promoPrice() {
      return this.currentBundle?.price || 0;
    },
    
    // show dbs switch if backend has not_at_home tests
    showDbsSwitch() {
      if (this.testList.some(item => item.section === 'not_at_home')) return true;
      return false;
    },
    isDbsToggleDisabled() {
      return this.isAddOnOrder.length !== 0;
    },

    
    // Get list of selected items (zoomers and panels) for heavy metal pricing calculation
    selectedItemsList() {
      const items = [];
      
      // Add selected zoomers
      this.selectedZoomerIds.forEach(id => {
        const zoomer = this.computedZoomerList.find(z => z.id === id);
        if (zoomer) items.push(zoomer);
      });
      
      // Add selected panels
      this.selectedPanelIds.forEach(id => {
        for (const cat of this.computedPanelCategories) {
          const foundPanel = cat.panels.find(p => (p.id || p.bundleId) === id);
          if (foundPanel) {
            items.push(foundPanel);
            break;
          }
        }
      });
      
      return items;
    },
    selectedZoomerNames() {
      return this.selectedZoomerIds
        .map(id => this.computedZoomerList.find(z => z.id === id))
        .filter(Boolean)
        .map(item => item.displayName || item.name || item.bundleName || "");
    },
    selectedPanelNames() {
      const panelList = this.computedPanelCategories.flatMap(c => c.panels);
      return this.selectedPanelIds
        .map(id => panelList.find(p => (p.id || p.bundleId) === id))
        .filter(Boolean)
        .map(item => item.displayName || item.name || item.bundleName || "");
    },
    
    computedZoomerList() {
      if (!this.currentBundle) return [];
      const packageIds = this.currentBundle.bundleContent?.packageList?.map(p => p.packageId) || [];
      return this.testList.filter(t => packageIds.includes(t.id));
    },

    
    computedPanelCategories() {
      if (!this.currentBundle) return [];
      const bundle = this.currentBundle;
      if (bundle.supplementContent && (bundle.supplementContent.itemIdList || bundle.supplementContent.bundleIdList)) {
        const suppItemIds = bundle.supplementContent.itemIdList || [];
        const suppPanelIds = bundle.supplementContent.bundleIdList || [];
        const categorizedList = this.dbsToggle ? this.dbsCategorizedVwTests : this.serumCategorizedVwTests;
        
        let categories = [];
        
        categorizedList.forEach(category => {
          category.testNodeList.forEach(node => {
            const matchedTests = [];
            // Check if root node itself matches
            if (suppItemIds.includes(node.id)) {
              matchedTests.push(node.value);
            }
            
            // Check descendants
            let descendants = [];
            getAllDescendants(node, descendants);
            descendants.forEach(d => {
              if (suppItemIds.includes(d.id) && !matchedTests.some(t => t.id === d.id)) {
                matchedTests.push(d.value);
              }
            });
            
            if (matchedTests.length > 0) {
              categories.push({
                id: node.id,
                name: node.value.displayName || node.value.name,
                panels: matchedTests
              });
            }
          });
        });

        // Also include panels if bundleIdList exists
        if (suppPanelIds.length > 0) {
          const suppPanels = this.bundleList.filter(b => suppPanelIds.includes(b.bundleId));
          if (suppPanels.length > 0) {
            categories.push({
              id: 'supp-panels',
              name: "Available Panels",
              panels: suppPanels
            });
          }
        }
        return categories;
      }
      return [];
    },

    // Suggestion's selected main and supplement ids (reused from initializeSelection); used to exclude prefill ids from xChooseY blocking
    suggestionPrefillSelection() {
      const r = this.suggestionPrefillResult;
      if (!r) return { selectedMainIds: new Set(), selectedSupplementIds: new Set() };
      return {
        selectedMainIds: new Set(r.selectedMainIds || []),
        selectedSupplementIds: new Set(r.selectedSupplementIds || [])
      };
    },

    // Get all IDs that are already in xChooseY bundles; exclude ids that are in suggestion's selectedMainIds so prefill zoomers are not disabled
    xChooseYZoomerIds() {
      const ids = new Set();
      const prefillMain = this.suggestionPrefillSelection.selectedMainIds;
      this.selectedXChooseYPanelList.forEach(panel => {
        if (panel.selectedZoomerIdList) {
          panel.selectedZoomerIdList.forEach(id => {
            if (!prefillMain.has(id)) ids.add(id);
          });
        }
      });
      return ids;
    },

    // Get all IDs that are already in xChooseY bundles; exclude ids that are in suggestion's selectedSupplementIds so prefill panels are not disabled
    xChooseYPanelIds() {
      const ids = new Set();
      const prefillSupplement = this.suggestionPrefillSelection.selectedSupplementIds;
      this.selectedXChooseYPanelList.forEach(panel => {
        if (panel.selectedSupplementIdList) {
          panel.selectedSupplementIdList.forEach(id => {
            if (!prefillSupplement.has(id)) ids.add(id);
          });
        }
      });
      return ids;
    }
  },
  watch: {
    bundleId: {
      immediate: true,
      handler(newBundleId) {
        this.currentBundleId = newBundleId;
      }
    },
    selectedItemsList: {
      handler() {
        this.applyHeavyMetalPricing();
      },
      deep: true
    }
  },
  mounted() {
    this.initializeSelection();
    // Apply heavy metal pricing after initialization
    this.$nextTick(() => {
      this.applyHeavyMetalPricing();
    });
  },
  methods: {
    applyHeavyMetalPricing() {
      // Combine selected items with xChooseY zoomers
      const allItems = [...this.selectedItemsList];
      
      // Add zoomers from xChooseY bundles
      this.xChooseYZoomerIds.forEach(id => {
        const zoomer = this.testList.find(t => t.id === id);
        if (zoomer && !allItems.some(item => item.id === zoomer.id)) {
          allItems.push(zoomer);
        }
      });
      // set isSelect to true for selected items from build bundle dialog to ensure conditional discounts are applied correctly
      allItems.forEach(item => {
        item.isSelect = true;
      });
      applyConditionalDiscounts(this.testList, "isSelect", allItems);
    },
    
    deselectChildren(itemId) {
      const itemNode = this.bfsTestList.find(n => n.id === itemId);
      if (!itemNode) return;
      
      let descendants = [];
      getAllDescendants(itemNode, descendants);
      const descendantIds = descendants.map(d => d.id);
      
      // Remove descendants from both zoomer and panel arrays
      this.selectedZoomerIds = this.selectedZoomerIds.filter(id => !descendantIds.includes(id));
      this.selectedPanelIds = this.selectedPanelIds.filter(id => !descendantIds.includes(id));
    },
    
    handleSelectItem(item, type) {
      const itemId = item.id || item.bundleId;
      if (!itemId) return;
      
      // Use the type parameter to determine which array to use
      const selectedArray = type === 'zoomer' ? this.selectedZoomerIds : this.selectedPanelIds;
      
      const index = selectedArray.indexOf(itemId);
      if (index > -1) {
        // Deselect
        selectedArray.splice(index, 1);
      } else {
        // Select
        selectedArray.push(itemId);
        // Deselect any children (panels or zoomers)
        this.deselectChildren(itemId);
      }
    },

    isAllowedForPatientAge(test) {
      return isItemAllowedForPatientAge(test, this.supportObj, this.patientAge);
    },
    
    isItemDisabledByOtherItems(item) {
      const itemId = item.id || item.bundleId;
      if (!itemId) return false;

      // Combine both arrays for simpler logic
      const allSelectedIds = [...this.selectedZoomerIds, ...this.selectedPanelIds];

      // Go through each selected item and check if the current item is a descendant
      return allSelectedIds.some(selectedId => {
        if (selectedId === itemId) return false;
        
        const selectedNode = this.bfsTestList.find(n => n.id === selectedId);
        if (!selectedNode) return false;
        
        let descendants = [];
        getAllDescendants(selectedNode, descendants);
        return descendants.some(d => d.id === itemId);
      });
    },


    isZoomerInXChooseY(zoomer) {
      return this.xChooseYZoomerIds.has(zoomer.id);
    },

    isPanelInXChooseY(panel) {
      const panelId = panel.id || panel.bundleId;
      if (!panelId) return false;
      
      // Check if the panel ID itself is in xChooseY
      if (this.xChooseYPanelIds.has(panelId)) return true;
      
      // Also check if any zoomer in xChooseY has this panel as a descendant
      // (because if a zoomer is in xChooseY, its children panels are considered unavailable)
      return Array.from(this.xChooseYZoomerIds).some(zoomerId => {
        const zoomerNode = this.bfsTestList.find(n => n.id === zoomerId);
        if (!zoomerNode) return false;
        
        let descendants = [];
        getAllDescendants(zoomerNode, descendants);
        return descendants.some(d => d.id === panelId);
      });
    },

    /**
     * All disable conditions for a panel card (age, other items, xChooseY, no remaining quota,
     * or "only one remaining in category" to prevent completing the bundle from a single category).
     */
    getPanelCardDisabled(panel, category) {
      const panelId = panel.id || panel.bundleId;
      if (!this.isAllowedForPatientAge(panel)) return true;
      if (this.isItemDisabledByOtherItems(panel)) return true;
      if (this.isPanelInXChooseY(panel)) return true;
      if (!this.selectedPanelIds.includes(panelId) && this.remainingPanelQuantity === 0) return true;
      if (this.isPanelDisabledAsOnlyRemainingInCategory(panel, category)) return true;
      return false;
    },

    /**
     * Disable a panel when it is the only unselected panel in its category.
     * This prevents the user from completing the bundle by selecting all panels from the same category.
     * E.g. category has 2 panels, 1 selected, user needs 1 more -> disable the other so they must pick from another category.
     * If category has 3 panels and 1 is selected, the 2 unselected remain enabled (user can pick one of two).
     */
    isPanelDisabledAsOnlyRemainingInCategory(panel, category) {
      if (!category?.panels?.length) return false;
      const panelId = panel.id || panel.bundleId;
      if (this.selectedPanelIds.includes(panelId)) return false;
      if (this.remainingPanelQuantity === 0) return false;

      const unselectedInCategory = category.panels.filter(p => !this.selectedPanelIds.includes(p.id || p.bundleId));

      if (unselectedInCategory.length !== 1) return false;
      return (unselectedInCategory[0].id || unselectedInCategory[0].bundleId) === panelId;
    },

    changeDbsVersion(dbsVersion) {
      const selectedZoomerIdsBeforeToggle = [...this.selectedZoomerIds];
      const selectedPanelIdsBeforeToggle = [...this.selectedPanelIds];
      this.dbsToggle = dbsVersion;
      // Update currentBundleId to the corresponding version
      if (this.currentBundleId) {
        if (dbsVersion) {
          // Switching to DBS: find DBS version
          const dbsMapping = this.dbsMap.find(dbs => dbs.originalItemId === this.currentBundleId && dbs.type === 'bundle');
          if (dbsMapping) {
            this.currentBundleId = dbsMapping.dbsItemId;
          }
        } else {
          // Switching to serum: find serum version
          const dbsMapping = this.dbsMap.find(dbs => dbs.dbsItemId === this.currentBundleId && dbs.type === 'bundle');
          if (dbsMapping) {
            this.currentBundleId = dbsMapping.originalItemId;
          }
        }
      }
      const mapToTargetMenuId = (id) => {
        if (dbsVersion) {
          const mappedItem = this.dbsMap.find(dbs => dbs.originalItemId === id);
          return mappedItem ? mappedItem.dbsItemId : id;
        }
        const mappedItem = this.dbsMap.find(dbs => dbs.dbsItemId === id);
        return mappedItem ? mappedItem.originalItemId : id;
      };
      this.selectedZoomerIds = selectedZoomerIdsBeforeToggle.map(mapToTargetMenuId);
      this.selectedPanelIds = selectedPanelIdsBeforeToggle.map(mapToTargetMenuId);
      // clean current selection if not in changed menu
      this.removeTestsNotInCurrentMenu();
    },
    toggleHomeCollection() {
      if (this.isDbsToggleDisabled) return;
      this.changeDbsVersion(!this.dbsToggle);
    },

    removeTestsNotInCurrentMenu() {
      const currentZoomerIds = this.computedZoomerList.map(z => z.id);
      const currentPanelIds = this.computedPanelCategories.flatMap(c => c.panels.map(p => p.id || p.bundleId));
      
      this.selectedZoomerIds = this.selectedZoomerIds.filter(id => currentZoomerIds.includes(id));
      this.selectedPanelIds = this.selectedPanelIds.filter(id => currentPanelIds.includes(id));
    },
    
    handleCancel() {
      // Emit both close and cancel for flexibility
      // close: standard dialog close behavior
      // cancel: allows parent to handle cancel-specific logic
      this.$emit('close');
      this.$emit('cancel');
    },
    handleAddToCart() {
      this.$emit('add-to-cart', {
        zoomerIds: this.selectedZoomerIds,
        panelIds: this.selectedPanelIds,
        dbsToggle: this.dbsToggle
      });
    },

    findSuggestionForCurrentBundle() {
      if (!this.currentBundle) return { selectedMainIds: [], selectedSupplementIds: [], ineligible: true };
      const suggestion = {
        bundleContentIds: (this.currentBundle.bundleContent?.packageList?.map(p => p.packageId) || []).concat((this.currentBundle.bundleContent?.testList || []).map(t => t.testId)),
        supplementList: (this.currentBundle.supplementContent?.itemIdList || []).concat(this.currentBundle.supplementContent?.bundleIdList || []),
        quantity: this.currentBundle.quantity,
        supplementQuantity: this.currentBundle.supplementQuantity
      };
      if (!suggestion.bundleContentIds?.length && !suggestion.supplementList?.length) {
        return { selectedMainIds: [], selectedSupplementIds: [], ineligible: true };
      }
      const testIds = getTestIdsForBestDealFunc(this.orderTestSupportObj);
      const selectedPanelList = this.orderTestSupportObj?.selectedItemList?.filter(item => item.category === "discountPanel" || item.category === "xChooseY") || [];
      const testNodeList = this.orderTestSupportObj?.testNodeList || [];
      return getSelectedTestIdsInSuggestion(suggestion, testIds, testNodeList, selectedPanelList, this.orderTestSupportObj);
    },

    initializeSelection() {
      const { selectedTestIdList, selectedItemList } = this.orderTestSupportObj;
      
      const zoomerPool = this.computedZoomerList;
      const zoomerPoolIds = zoomerPool.map(z => z.id);
      
      const panelPool = this.computedPanelCategories.flatMap(c => c.panels);
      const panelPoolIds = panelPool.map(p => p.id || p.bundleId);

      let eligibleZoomerIds = new Set();
      let eligiblePanelIds = new Set();

      const addIfEligible = (id) => {
        if (zoomerPoolIds.includes(id)) eligibleZoomerIds.add(id);
        else if (panelPoolIds.includes(id)) eligiblePanelIds.add(id);
        else {
          const node = this.bfsTestList.find(n => n.id === id);
          if (node) {
            let descendants = [];
            getAllDescendants(node, descendants);
            descendants.forEach(d => {
              if (zoomerPoolIds.includes(d.id)) eligibleZoomerIds.add(d.id);
              else if (panelPoolIds.includes(d.id)) eligiblePanelIds.add(d.id);
            });
          }
        }
      };

      const suggestionResult = this.findSuggestionForCurrentBundle();
      if (!suggestionResult.ineligible && (suggestionResult.selectedMainIds?.length > 0 || suggestionResult.selectedSupplementIds?.length > 0)) {
        (suggestionResult.selectedMainIds || []).forEach(id => addIfEligible(id));
        (suggestionResult.selectedSupplementIds || []).forEach(id => addIfEligible(id));
      } else {
        selectedTestIdList.forEach(id => addIfEligible(id));
        selectedItemList.filter(item => item.category === "discountPanel").forEach(panel => {
          if (panelPoolIds.includes(panel.bundleId)) eligiblePanelIds.add(panel.bundleId);
          else if (panel.testIdsInPanel) {
            panel.testIdsInPanel.forEach(id => addIfEligible(id));
          }
        });
      }

      this.suggestionPrefillResult = {
        selectedMainIds: [...(suggestionResult.selectedMainIds || [])],
        selectedSupplementIds: [...(suggestionResult.selectedSupplementIds || [])]
      };

      // 2. Filter out items that are already in xChooseY bundles
      // Remove zoomers that are in xChooseY
      this.xChooseYZoomerIds.forEach(id => {
        eligibleZoomerIds.delete(id);
      });
      
      // Remove panels that are in xChooseY or are descendants of zoomers in xChooseY
      const panelsToRemove = new Set();
      eligiblePanelIds.forEach(panelId => {
        if (this.xChooseYPanelIds.has(panelId)) {
          panelsToRemove.add(panelId);
        } else {
          // Check if this panel is a descendant of any zoomer in xChooseY
          const isDescendantOfXChooseYZoomer = Array.from(this.xChooseYZoomerIds).some(zoomerId => {
            const zoomerNode = this.bfsTestList.find(n => n.id === zoomerId);
            if (!zoomerNode) return false;
            let descendants = [];
            getAllDescendants(zoomerNode, descendants);
            return descendants.some(d => d.id === panelId);
          });
          if (isDescendantOfXChooseYZoomer) {
            panelsToRemove.add(panelId);
          }
        }
      });
      panelsToRemove.forEach(id => eligiblePanelIds.delete(id));

      // 3. Map to objects and sort by price descending to prioritize most expensive items
      const sortedEligibleZoomers = Array.from(eligibleZoomerIds)
        .map(id => zoomerPool.find(z => z.id === id))
        .filter(Boolean)
        .sort((a, b) => b.price - a.price);

      const sortedEligiblePanels = Array.from(eligiblePanelIds)
        .map(id => panelPool.find(p => (p.id || p.bundleId) === id))
        .filter(Boolean)
        .sort((a, b) => b.price - a.price);

      // 4. Greedy selection up to zoomer limit
      this.selectedZoomerIds = sortedEligibleZoomers.slice(0, this.zoomerQuantity).map(z => z.id);
      
      // 5. Greedy selection up to panel limit, respecting parent-child exclusion
      this.selectedPanelIds = [];
      for (const panel of sortedEligiblePanels) {
        if (this.selectedPanelIds.length >= this.panelQuantity) break;
        
        const panelId = panel.id || panel.bundleId;
        // Do not select panel if its parent zoomer is already selected
        const isDescendantOfSelectedZoomer = this.selectedZoomerIds.some(zoomerId => {
          const zoomerNode = this.bfsTestList.find(n => n.id === zoomerId);
          if (!zoomerNode) return false;
          let descendants = [];
          getAllDescendants(zoomerNode, descendants);
          return descendants.some(d => d.id === panelId);
        });

        if (!isDescendantOfSelectedZoomer) {
          this.selectedPanelIds.push(panelId);
        }
      }
    }
  }
};
</script>

<style scoped>
.section-title {
  color: var(--ContentMediumGrey, #999);
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;
  letter-spacing: 0.25px;
  text-transform: uppercase;
}

.category-title {
  color: var(--SecondaryBlack, #666);
  font-family: Inter, sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 500;
  line-height: 12px;
  letter-spacing: 0.25px;
}

.category-title p {
  margin: 0;
}

.build-bundle-dialog-content {
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 220px);
  min-height: 0;
}

.bundle-selection-scroll {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding-right: 6px;
  padding-bottom: 20px;
}

.bundle-selection-scroll {
  min-height: 500px;
}

.selection-block {
  background: transparent;
  border: none;
  border-radius: 0;
  padding: 0;
}

.empty-state-text {
  margin-top: 8px;
  color: #777;
  font-size: 13px;
}

.home-collection-toggle-wrapper {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  background: #e8f4fd;
  border-radius: 4px;
  margin-bottom: 10px;
}

.home-collection-label-wrapper {
  display: flex;
  align-items: center;
}

.home-collection-toggle-text {
  font-size: 14px;
  color: #374957;
}

.status-toggle {
  display: flex;
  align-items: center;
  gap: 8px;
}

.status-label {
  font-size: 12px;
  color: #999;
  font-weight: 500;
}

.status-label.active {
  color: #004879;
}

.toggle-switch {
  width: 44px;
  height: 24px;
  background: #c4c4c4;
  border-radius: 12px;
  cursor: pointer;
  position: relative;
  transition: background 0.2s ease;
}

.toggle-switch.active {
  background: #004879;
}

.toggle-track {
  width: 100%;
  height: 100%;
  position: relative;
}

.toggle-thumb {
  width: 20px;
  height: 20px;
  background: #fff;
  border-radius: 50%;
  position: absolute;
  top: 2px;
  left: 2px;
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.toggle-switch.active .toggle-thumb {
  left: 22px;
}

.toggle-switch.disabled {
  cursor: not-allowed;
  opacity: 0.6;
}

.modal-footer-actions {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
  border-top: 1px dashed #c4c4c4;
  margin-top: 4px;
  padding: 24px 0;
  flex-shrink: 0;
  background: #fff;
  position: sticky;
  bottom: 0;
  z-index: 2;
}
</style>

<style>
.el-dialog.build-bundle-dialog {
  --el-dialog-padding-primary: 0;
  padding: 0;
  border-radius: 10px;
  overflow: hidden;
}

.el-dialog.build-bundle-dialog .el-dialog__body {
  padding: 0;
}

.el-dialog.build-bundle-dialog .content {
  padding: 20px 24px 0;
}

.el-dialog.build-bundle-dialog .content .build-bundle-back-btn.login-btn {
  border: 1px solid #d10000;
  color: #d10000;
  background: #fff;
  height: 40px;
  min-width: 94px;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-size: 14px;
  font-weight: 500;
}

.el-dialog.build-bundle-dialog .content .build-bundle-add-btn.login-btn {
  border: 1px solid #004879;
  color: #fff;
  background: #004879;
  height: 40px;
  min-width: 136px;
  font-family: "IBM Plex Sans Hebrew", sans-serif;
  font-size: 14px;
  font-weight: 500;
}

.el-dialog.build-bundle-dialog .content .build-bundle-add-btn.login-btn.is-disabled,
.el-dialog.build-bundle-dialog .content .build-bundle-add-btn.login-btn.is-disabled:hover {
  border: 1px solid #e0e0e0 !important;
  background: #e0e0e0 !important;
  color: #525252 !important;
  cursor: not-allowed;
}
</style>
